import { AddBusiness, AttachMoney, Home, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { Card, Loader } from '../../../../components';
import { saveBuildContingency, getContractDocuments } from '../../../../fetch';
import { formatDate, formatMoney } from '../../../../helpers';
import { Item } from '../OptionItem';
import {
  ISelectedSpecOption,
  ISpec,
  ISpecOptionsDetail,
  ICategory,
  ReactSetState,
  IContractDocumentResults
} from '../../../../models';
import { ContractSelectionActions } from '../contract-selection-actions';
import { FinancingInformation } from '../financing-information';
import { UserContext } from '../../../../context';
import { useFormikContext } from 'formik';
import { PrintSelectionDetailsInfo } from './print-selection-details-info';
import { GridOption } from '../price-calculation-grid';
import { ContractDocuments } from '../ContractDocuments';
interface ISelectionDetailInfo {
  spec: ISpec;
  specOptions: ISpecOptionsDetail;
  setSaving: (val: boolean) => void;
  isSaving: boolean;
  selectedOptions: ISelectedSpecOption[];
  category: ICategory;
  setHouseSaleContingencyLocation: ReactSetState<string>;
  houseSaleContingencyLocation: string;
  setHasHouseSaleContingency: ReactSetState<boolean>;
  hasHouseSaleContingency: boolean;
  handleClose: () => void;
  path?: string;
  shouldShowFinishSpecButton?: boolean;
  fetchOptionDetails?: () => Promise<void>;
}

export const SelectionDetailInfo: FC<ISelectionDetailInfo> = ({
  spec,
  specOptions,
  setSaving,
  isSaving,
  selectedOptions,
  category,
  setHouseSaleContingencyLocation,
  houseSaleContingencyLocation,
  setHasHouseSaleContingency,
  hasHouseSaleContingency,
  handleClose,
  path,
  shouldShowFinishSpecButton = true,
  fetchOptionDetails
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(`(max-width: 900px)`);
  const [isFinishing, setIsFinishing] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get('redirect');
  const { user } = useContext(UserContext);
  const isClient = user?.userRoles?.includes('BL_Client');
  const isAdmin = user?.userRoles?.includes('BL_Admin');
  const isAdminOrSalesAdmin =
    user?.userRoles?.includes('BL_Admin') || user?.userRoles?.includes('BL_SalesAdmin');

  const {
    isLoading,
    isRefetching,
    data: contractDocumentResults,
    refetch: refetchContractDocumentResults
  } = useQuery<IContractDocumentResults, Error>(
    ['contract-documents-data', spec.buildId],
    async () => await getContractDocuments(spec.buildId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: typeof spec.buildId === 'number' && specOptions?.showContractDocuments
    }
  );

  const { values } = useFormikContext<{
    financingType: string;
    lenderId: string;
  }>();
  return (
    <>
      <Box display={'block'} displayPrint={'none'}>
        {isFinishing && (
          <Loader position="centered" type="overlay">
            Finishing Spec...
          </Loader>
        )}
        <Card icon={<Home />} title="Details">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Item label="Created On: " value={formatDate(specOptions?.createdOn)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item label="Status: " value={specOptions?.status} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item label="Purchaser: " value={specOptions?.client} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item label="Neighborhood: " value={spec?.neighborhoodDescription} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item label="Lot: " value={spec?.lotNumber} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Item label="Plan: " value={spec?.planDescription} />
            </Grid>
          </Grid>
        </Card>
        <Card icon={<AddBusiness />} title="House Sale Contingency Information">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isClient}
                    checked={hasHouseSaleContingency}
                    onChange={e => {
                      setHasHouseSaleContingency(e.target.checked);
                    }}
                  />
                }
                label="Has House Sale Contingency"
              />
            </Grid>
            <Grid display={'none'} displayPrint={'block'} item xs={6}>
              <div>Contingent House Location (If Applicable):</div>
              {houseSaleContingencyLocation ?? ''}
            </Grid>
            <Grid displayPrint={'none'} item xs={12} sm={8}>
              <TextField
                disabled={isClient}
                required={hasHouseSaleContingency}
                fullWidth
                variant="outlined"
                autoComplete="nope"
                label="Contingent House Location (If Applicable)"
                name="name"
                value={houseSaleContingencyLocation}
                size="small"
                onChange={e => setHouseSaleContingencyLocation(e.target.value)}
              />
            </Grid>
          </Grid>
          {!isClient && (
            <Box displayPrint={'none'} marginTop="2rem">
              <Button
                type="button"
                startIcon={<Save />}
                variant="contained"
                color="primary"
                disabled={isSaving || (hasHouseSaleContingency && !houseSaleContingencyLocation)}
                onClick={async () => {
                  try {
                    setSaving(true);
                    await saveBuildContingency(`${spec.buildId}`, {
                      hasHouseSaleContingency: hasHouseSaleContingency || false,
                      houseSaleContingencyLocation: houseSaleContingencyLocation || ''
                    });
                    refetchContractDocumentResults();
                    enqueueSnackbar(`Saved Contingency Info!`, {
                      variant: 'success'
                    });
                  } catch (error) {
                    enqueueSnackbar(
                      error?.Detail ?? `Error saving contingency info, please try again.`,
                      {
                        variant: 'error'
                      }
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Save Contingency Info
              </Button>
            </Box>
          )}
        </Card>
        {isAdminOrSalesAdmin && spec?.isContractComplete && (
          <FinancingInformation
            buildId={spec?.buildId}
            neighborhoodId={spec?.neighborhoodId}
            financingType={values?.financingType}
            lenderId={values?.lenderId}
            fetchOptionDetails={fetchOptionDetails}
          />
        )}
        <Card icon={<AttachMoney />} title="Price Calculations">
          <Grid
            container
            spacing={1}
            sx={!isMobile ? { maxWidth: '1270px', minWidth: '800px' } : {}}
          >
            {/* Headers */}
            <Grid item xs={12} md={9}>
              <GridOption.Header
                label=""
                originalPrice={'Original'}
                incentive={'Adjustments/Incentives'}
                finalPrice={'Total'}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Base Price:"
                originalPrice={formatMoney(specOptions?.basePrice_Original)}
                incentive={formatMoney(specOptions?.basePrice_Adjustment)}
                finalPrice={formatMoney(specOptions?.basePrice)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Lot Premium:"
                originalPrice={formatMoney(specOptions?.lotPremium_Original)}
                incentive={formatMoney(specOptions?.lotPremium_Adjustment)}
                finalPrice={formatMoney(specOptions?.lotPremium)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Options at Contract:"
                finalPrice={formatMoney(specOptions?.contractUpgradeAmount)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Ten Day Options:"
                finalPrice={formatMoney(specOptions?.tenDayOptionsPrice)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Incentives:"
                finalPrice={formatMoney(specOptions?.incentives)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Total Price:"
                finalPrice={formatMoney(specOptions?.totalPriceAdjusted)}
              />
            </Grid>

            <Grid item xs={12} md={9}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Option Deposit Cap:"
                originalPrice={''}
                finalPrice={formatMoney(specOptions?.appraisalAlarmValue)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <GridOption.Row
                label="Option Deposit Cap Overage:*"
                color={
                  specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                    ? 'red'
                    : undefined
                }
                finalPrice={formatMoney(specOptions?.upgradeCapOverage)}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography
                variant="caption"
                sx={{
                  color:
                    specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                      ? 'red'
                      : undefined
                }}
              >
                *Deposit amount due based on selected options.
              </Typography>
              <br />
              <Typography
                variant="caption"
                sx={{
                  color:
                    specOptions?.upgradeCapOverage > specOptions?.appraisalAlarmValue
                      ? 'red'
                      : undefined
                }}
              >
                Any additional options will be added to the amount due at final contract acceptance.
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {specOptions?.showContractDocuments && <ContractSelectionActions spec={spec} />}

        {specOptions?.showContractDocuments && (
          <ContractDocuments
            spec={spec}
            specOptions={specOptions}
            isSaving={isSaving}
            setSaving={setSaving}
            onClose={handleClose}
            isAdmin={isAdmin}
            isFinishing={isFinishing}
            setIsFinishing={setIsFinishing}
            redirect={redirect}
            path={path}
            isLoading={isLoading}
            isRefetching={isRefetching}
            contractDocumentResults={contractDocumentResults}
            refetchContractDocumentResults={refetchContractDocumentResults}
            shouldShowFinishSpecButton={shouldShowFinishSpecButton}
          />
        )}
      </Box>
      <Box display={'none'} displayPrint={'block'}>
        <PrintSelectionDetailsInfo
          spec={spec}
          specOptions={specOptions}
          houseSaleContingencyLocation={houseSaleContingencyLocation}
          hasHouseSaleContingency={hasHouseSaleContingency}
          values={values}
        />
      </Box>
    </>
  );
};
